import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UtilService } from '../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from '../../services/error.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { Pagination } from '../../services/api/api';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { SizeService } from '../../services/size-detection/size.service';
import { MsOptions } from '../../services/global/global-interfaces';
import { takeUntil } from 'rxjs/operators';
import { StringTransformationService } from '../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-comp-string-dropdown-select',
  templateUrl: './sb-string-dropdown-select.component.html',
  styleUrls: ['./sb-string-dropdown-select.component.scss'],
  standalone: false,
})
export class SbStringDropdownSelectComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() tooltip: string;
  @Input() showText: boolean = true;
  @Input() showIcon: boolean = true;
  @Input() showColumnIcon: boolean = true;
  @Input() showSubmit: boolean = true;
  @Input() dropDownDatatype: string;
  @Input() showChevronDown: boolean = true;
  @Input() singleSelectMode: boolean = false;
  @Input() disabled: boolean = false;
  @Input() hidePagination: boolean = false;
  @Input() frontendSearch: boolean = false;

  @Output() selectedDataEmitter: EventEmitter<string[]>;
  @Output() msOptionsChange: EventEmitter<MsOptions> = new EventEmitter<MsOptions>();

  @ViewChild('dropdown') dropdown: NgbDropdown;

  // tslint:disable-next-line:variable-name
  _msOptions: MsOptions = {
    search: null,
    pagination: null,
  };
  dropDownData: string[];
  filteredDropDownData: string[];
  // tslint:disable-next-line:variable-name
  _page: number;
  paginationInitialized: boolean;
  maxSize: number;
  paginationString: string;
  boundaryLinks: boolean;
  collectionSize: number;
  resultCount: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  debounceSearchTimer: ReturnType<typeof setTimeout> = null;
  selectedData: string[];

  submitted: boolean;
  showModalLike: boolean;
  private modalOption: boolean;
  private readonly destroy$: Subject<void> = new Subject<void>();

  @Input() set inputData(values: string[]) {
    this.dropDownData = [];
    if (values != null) {
      this.dropDownData = values;
      this.filteredDropDownData = values;
    }
    if (this.msOptions.pagination) {
      this.paginationInitialized = true;
    }
    this.paginationString = this.util.getPaginationString(
      this.page,
      this.maxSize,
      this.dropDownData?.length,
      this.resultCount,
      this.dropDownDatatype ? this.stringTransformation.toCamelCase(this.dropDownDatatype) : null
    );
    this.destroySub();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginationString = this.util.getPaginationString(
        this.page,
        this.maxSize,
        this.dropDownData?.length,
        this.resultCount,
        this.dropDownDatatype ? this.stringTransformation.toCamelCase(this.dropDownDatatype) : null
      );
    });
  }

  @Input() set selectedInputData(values: string[]) {
    this.selectedData = [];
    if (values != null) {
      this.selectedData = values;
    }
  }

  @Input() set msOptions(val: MsOptions) {
    if (val) {
      if (val.pagination) {
        this._msOptions.pagination = val.pagination;
      }
      if (val.search) {
        this._msOptions.search = val.search;
      }
    }
  }

  get msOptions(): MsOptions {
    return this._msOptions;
  }

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.msOptions.pagination.value.page = val;
  }

  constructor(
    public util: UtilService,
    public translate: TranslateService,
    public errorHandler: ErrorService,
    public sizeService: SizeService
  ) {
    this.selectedDataEmitter = new EventEmitter<string[]>();
    this.selectedData = [];
    this.dropDownData = [];
    this._msOptions = {
      search: null,
      pagination: null,
    };
    this._page = 1;
    this.maxSize = 8;
    this.collectionSize = -1;
    this.resultCount = -1;

    this.paginationInitialized = false;
    this.paginationString = '';
    this.boundaryLinks = true;
    this.submitted = false;
    this.showModalLike = false;
    this.modalOption = false;

    this._msOptions.pagination = new BehaviorSubject<Pagination>({
      page: this._page,
      count: this.maxSize ? this.maxSize : 10,
      pages: -1,
      resultcount: -1,
    });
  }

  ngOnInit(): void {
    this.modalOption = this.sizeService.checkScreensize(['md', 'sm', 'xs']);
    this.sizeService.onResize$().subscribe(() => {
      if (this.dropdown?._open) {
        this.dropdown.close();
      }
    });
    this.initPagination().then(() => {
      this.requestDebounced();
    });
  }

  destroySub() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  initPagination(): Promise<void> {
    return new Promise(res => {
      this.msOptions.pagination.subscribe((e: Pagination) => {
        this.collectionSize = e.resultcount;
        this.maxSize = e.count;
        this.resultCount = e.resultcount;
      });
      res();
    });
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      // options push
      if (this.msOptions?.pagination) {
        this.msOptionsChange.emit(this.msOptions);
      }
    }, 500);
  }

  deselectAll() {
    this.selectedData = [];
    if (!this.showSubmit) {
      this.prepareOutput();
    }
    if (this.singleSelectMode) {
      if (!this.modalOption) {
        this.dropdown.close();
      } else {
        this.toggleModalDropdownSelect();
      }
      this.prepareOutput();
    }
  }

  checkColumn($event, data: string) {
    $event.stopPropagation();
    $event.preventDefault();
    if (!this.selectedData) {
      this.selectedData = [];
    }

    if (!this.singleSelectMode) {
      if (!this.selectedData.includes(data)) {
        this.selectedData.push(data);
      } else {
        this.selectedData.splice(this.selectedData.indexOf(data), 1);
      }

      if (!this.showSubmit) {
        this.prepareOutput();
      }
    } else {
      if (!this.selectedData.includes(data)) {
        this.selectedData = [data];
      } else {
        this.selectedData.splice(0, 1);
      }

      if (!this.modalOption) {
        this.dropdown.close();
      } else {
        this.toggleModalDropdownSelect();
      }
      this.prepareOutput();
    }
  }

  submit() {
    this.submitted = true;
    if (!this.modalOption) {
      this.dropdown.close();
    } else {
      this.toggleModalDropdownSelect();
    }
    this.prepareOutput();
  }

  closeCall(event) {
    if (!this.singleSelectMode) {
      if (!event && !this.submitted) {
        this.selectedData = [];
      }
      if (this.submitted) {
        this.submitted = false;
      }
    }
  }

  prepareOutput() {
    this.selectedDataEmitter.emit(this.selectedData);
  }

  getClass(): string {
    if (this.selectedData.length > 0) {
      return 'btn-hover-primary--active';
    }
    return 'btn-hover-primary';
  }

  getColumnIcon() {
    switch (this.dropDownDatatype) {
      case 'TAGS':
        return 'tags';
      case 'ATTRIBUTE':
        return 'attribute';
      default:
        return 'settings';
    }
  }

  getButtonText(): string {
    if (this.selectedData?.length === 1) {
      return this.selectedData[0];
    }

    switch (this.dropDownDatatype) {
      case 'TAGS':
        if (this.selectedData?.length === 0) {
          return 'Tags';
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + 'Tags';
        }
        break;
      case 'ATTRIBUTE':
        if (this.selectedData?.length === 0) {
          return this.translate.instant('lang.globals.noSelection');
        }
        if (this.selectedData?.length > 1) {
          return this.selectedData.length + ' ' + this.translate.instant('lang.globals.selectedValues');
        }
        break;

      default:
        return 'Unknown Datatype';
    }
  }

  getHeadline(): string {
    switch (this.dropDownDatatype) {
      case 'TAGS':
        return 'Tags';
      case 'ATTRIBUTE':
        return this.translate.instant('lang.attributedatatypes.allowedValues');
      default:
        return 'Unknown Datatype';
    }
  }

  toggleModalDropdownSelect() {
    this.showModalLike = !this.showModalLike;
  }

  getButtonWidth(): string {
    let width: number = 42;
    width += this.showIcon ? 20 : 0;
    width += this.showText ? (this.getHeadline().length + 2) * 9 : 0;
    width += this.showChevronDown ? 10 : 0;
    return width + 'px';
  }

  searchLocally() {
    if (this.debounceSearchTimer) {
      clearTimeout(this.debounceSearchTimer);
    }
    this.debounceSearchTimer = setTimeout(() => {
      if (this.frontendSearch) {
        this.filteredDropDownData = this.dropDownData.filter(data => data.includes(this.msOptions.search));
      }
    }, 500);
  }
}
